import { Controller } from "@hotwired/stimulus"
import apiClient from "@/entrypoints/frontend/apiClient"

export default class extends Controller {
  static values = {
    url: String,
    startAllResultsUrl: String,
    skipTrusteeUrl: String,
    interval: { type: Number, default: 4000 },
    confirmSkipMessage: String,
    belowThresholdMessage: String,
    extractionGrouping: String,
  }

  static targets = [
    "overallProgress",
    "overallPercentage",
    "votingRoundRow",
    "downloadButton",
    "downloadAuditsButton",
    "lastUpdated",
    "trusteeCard",
    "trusteeSkipButton",
    "completionBanner",
    "thresholdBanner",
    "thresholdMessage",
    "extractionGroupingSelect",
    "startButton",
  ]

  declare urlValue: string
  declare startAllResultsUrlValue: string
  declare skipTrusteeUrlValue: string
  declare intervalValue: number
  declare confirmSkipMessageValue: string
  declare belowThresholdMessageValue: string
  declare extractionGroupingValue: string
  declare overallProgressTarget: HTMLElement
  declare overallPercentageTarget: HTMLElement
  declare votingRoundRowTargets: HTMLElement[]
  declare downloadButtonTarget: HTMLElement
  declare lastUpdatedTarget: HTMLElement
  declare trusteeCardTargets: HTMLElement[]
  declare hasDownloadButtonTarget: boolean
  declare hasDownloadAuditsButtonTarget: boolean
  declare downloadAuditsButtonTarget: HTMLElement
  declare hasLastUpdatedTarget: boolean
  declare completionBannerTarget: HTMLElement
  declare hasCompletionBannerTarget: boolean
  declare thresholdBannerTarget: HTMLElement
  declare hasThresholdBannerTarget: boolean
  declare thresholdMessageTarget: HTMLElement
  declare hasThresholdMessageTarget: boolean
  declare hasExtractionGroupingSelectTarget: boolean
  declare extractionGroupingSelectTarget: HTMLSelectElement
  declare hasStartButtonTarget: boolean
  declare startButtonTarget: HTMLButtonElement

  private timer: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  private autoResumeTriggered = false
  private previousThresholdMet: boolean | null = null

  connect() {
    this.poll()
  }

  disconnect() {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
  }

  async poll() {
    if (this.stopped) return

    try {
      const url = new URL(this.urlValue, window.location.origin)

      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return

      const data = await response.json()
      this.update(data)
    } catch {
      // silently retry on next interval
    } finally {
      if (!this.stopped) {
        this.timer = setTimeout(() => this.poll(), this.intervalValue)
      }
    }
  }

  update(data: {
    voting_rounds: Array<{
      id: number
      label: string
      status: string
      progress: number
      zero_votes: boolean
      error: string | null
      started_at: string | null
      updated_at: string | null
    }>
    overall_progress: number
    all_completed: boolean
    threshold_met: boolean
    connected_count: number
    threshold: number
    in_progress: boolean
    extraction_grouping: string | null
    trustees: Array<{
      id: number
      name: string
      connected: boolean
      can_skip: boolean
      skipped: boolean
    }>
  }) {
    const overallPct = Math.round(data.overall_progress * 100)
    this.overallProgressTarget.style.width = `${overallPct}%`
    this.overallProgressTarget.setAttribute("aria-valuenow", String(overallPct))
    this.overallPercentageTarget.textContent = `${overallPct}%`

    if (overallPct >= 100) {
      this.overallProgressTarget.classList.remove("progress-bar-striped", "progress-bar-animated")
    }

    for (const vrData of data.voting_rounds) {
      const row = this.votingRoundRowTargets.find(
        (el) => el.dataset.votingRoundId === String(vrData.id)
      )
      if (!row) {
        window.location.reload()
        return
      }

      const statusBadge = row.querySelector("[data-bulk-counting-target='statusBadge']") as HTMLElement
      if (statusBadge) {
        statusBadge.textContent = statusBadge.dataset[`label${this.capitalize(vrData.status)}`] || vrData.status
        statusBadge.className = `badge text-bg-${this.statusColor(vrData.status)}`
      }

      const zeroVotesBadge = row.querySelector("[data-bulk-counting-target='zeroVotesBadge']") as HTMLElement
      if (zeroVotesBadge) {
        if (vrData.zero_votes && vrData.status === "completed") {
          zeroVotesBadge.classList.remove("d-none")
        } else {
          zeroVotesBadge.classList.add("d-none")
        }
      }

      const progressBar = row.querySelector("[data-bulk-counting-target='progressBar']") as HTMLElement
      if (progressBar) {
        const pct = Math.round(vrData.progress * 100)
        progressBar.style.width = `${pct}%`
        progressBar.setAttribute("aria-valuenow", String(pct))
        progressBar.textContent = `${pct}%`
        if (pct >= 100) {
          progressBar.classList.remove("progress-bar-striped", "progress-bar-animated")
        }
      }

      const startedAt = row.querySelector("[data-bulk-counting-target='startedAt']") as HTMLElement
      if (startedAt && vrData.started_at) {
        startedAt.textContent = new Date(vrData.started_at).toLocaleString()
      }

      const errorCell = row.querySelector("[data-bulk-counting-target='errorCell']") as HTMLElement
      if (errorCell) {
        if (vrData.error) {
          errorCell.innerHTML = `<span class="text-danger small">${this.escapeHtml(vrData.error).substring(0, 100)}</span>`
        } else {
          errorCell.textContent = ""
        }
      }
    }

    if (data.trustees) {
      this.updateTrustees(data.trustees)
    }

    if (this.hasDownloadButtonTarget) {
      if (data.all_completed) {
        this.downloadButtonTarget.classList.remove("disabled")
        this.downloadButtonTarget.removeAttribute("aria-disabled")
      } else {
        this.downloadButtonTarget.classList.add("disabled")
        this.downloadButtonTarget.setAttribute("aria-disabled", "true")
      }
    }

    if (this.hasDownloadAuditsButtonTarget) {
      if (data.all_completed) {
        this.downloadAuditsButtonTarget.classList.remove("disabled")
        this.downloadAuditsButtonTarget.removeAttribute("aria-disabled")
      } else {
        this.downloadAuditsButtonTarget.classList.add("disabled")
        this.downloadAuditsButtonTarget.setAttribute("aria-disabled", "true")
      }
    }

    if (data.extraction_grouping) {
      this.extractionGroupingValue = data.extraction_grouping
      if (this.hasExtractionGroupingSelectTarget) {
        this.extractionGroupingSelectTarget.value = data.extraction_grouping
        this.extractionGroupingSelectTarget.disabled = true
      }
      if (this.hasStartButtonTarget) {
        this.startButtonTarget.disabled = true
      }
    }

    if (this.hasLastUpdatedTarget) {
      this.lastUpdatedTarget.textContent = new Date().toLocaleTimeString()
    }

    if (this.hasThresholdBannerTarget && !data.all_completed) {
      const thresholdMetChanged = this.previousThresholdMet !== null && this.previousThresholdMet !== data.threshold_met
      this.previousThresholdMet = data.threshold_met

      if (data.threshold_met) {
        this.thresholdBannerTarget.classList.add("d-none")
        if (thresholdMetChanged) document.querySelector(".flash-messages")?.remove()

        if (!data.in_progress && !this.autoResumeTriggered) {
          this.autoResumeTriggered = true
          this.autoResumeCounting()
        }
      } else {
        const msg = this.belowThresholdMessageValue
          .replace("__CONNECTED__", String(data.connected_count))
          .replace("__THRESHOLD__", String(data.threshold))
        if (this.hasThresholdMessageTarget) {
          this.thresholdMessageTarget.textContent = msg
        }
        this.thresholdBannerTarget.classList.remove("d-none")
        if (thresholdMetChanged) document.querySelector(".flash-messages")?.remove()
      }
    }

    if (data.all_completed) {
      this.stopped = true
      if (this.timer) clearTimeout(this.timer)

      if (this.hasThresholdBannerTarget) {
        this.thresholdBannerTarget.classList.add("d-none")
      }
      if (this.hasCompletionBannerTarget) {
        this.completionBannerTarget.classList.remove("d-none")
      }
      document.querySelector(".flash-messages")?.remove()
    } else if (this.hasCompletionBannerTarget) {
      this.completionBannerTarget.classList.add("d-none")
    }
  }

  updateTrustees(trustees: Array<{ id: number; name: string; connected: boolean; can_skip: boolean; skipped: boolean }>) {
    for (const trusteeData of trustees) {
      const card = this.trusteeCardTargets.find(
        (el) => el.dataset.trusteeId === String(trusteeData.id)
      )
      if (!card) continue

      const statusDot = card.querySelector("[data-bulk-counting-target='trusteeStatus']") as HTMLElement
      if (statusDot) {
        if (trusteeData.skipped) {
          statusDot.className = "rounded-circle bg-secondary"
        } else {
          statusDot.className = `rounded-circle ${trusteeData.connected ? "bg-success" : "bg-secondary"}`
        }
      }

      const connectionBadge = card.querySelector("[data-bulk-counting-target='trusteeConnectionBadge']") as HTMLElement
      if (connectionBadge) {
        if (trusteeData.skipped) {
          const skippedLabel = connectionBadge.dataset.labelSkipped || connectionBadge.textContent || ""
          connectionBadge.textContent = skippedLabel
          connectionBadge.className = "badge text-bg-warning"
        } else {
          const connectedLabel = connectionBadge.dataset.labelConnected || connectionBadge.textContent || ""
          const notConnectedLabel = connectionBadge.dataset.labelNotConnected || connectionBadge.textContent || ""
          connectionBadge.textContent = trusteeData.connected ? connectedLabel : notConnectedLabel
          connectionBadge.className = `badge text-bg-${trusteeData.connected ? "success" : "secondary"}`
        }
      }

      const skipButton = card.querySelector("[data-bulk-counting-target='trusteeSkipButton']") as HTMLButtonElement
      if (skipButton) {
        if (trusteeData.can_skip) {
          skipButton.classList.remove("d-none")
          skipButton.removeAttribute("disabled")
          skipButton.textContent = skipButton.dataset.skipLabel || "Skip"
        } else {
          skipButton.classList.add("d-none")
        }
      }
    }
  }

  async skipTrustee(event: Event) {
    const button = event.currentTarget as HTMLElement
    const trusteeId = button.dataset.trusteeId
    const trusteeName = button.dataset.trusteeName

    const message = this.confirmSkipMessageValue.replace("%{name}", trusteeName || "")
    if (!confirm(message)) return

    button.classList.add("d-none")

    const card = button.closest("[data-bulk-counting-target='trusteeCard']") as HTMLElement
    if (card) {
      const badge = card.querySelector("[data-bulk-counting-target='trusteeConnectionBadge']") as HTMLElement
      if (badge) {
        badge.textContent = badge.dataset.labelSkipped || "Skipped for all VRs"
        badge.className = "badge text-bg-warning"
      }
    }

    try {
      await apiClient.post(this.skipTrusteeUrlValue, { trustee_id: trusteeId })
    } catch {
      // the next poll re-renders the trustee from the server state
    }

    this.poll()
  }

  refresh() {
    this.poll()
  }

  private async autoResumeCounting() {
    if (!this.extractionGroupingValue) {
      this.autoResumeTriggered = false
      return
    }

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    if (!csrfToken) {
      this.autoResumeTriggered = false
      return
    }

    try {
      const url = new URL(this.startAllResultsUrlValue, window.location.origin)
      url.searchParams.set("extraction_grouping", this.extractionGroupingValue)

      const response = await fetch(url.toString(), {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken },
        credentials: "same-origin",
      })

      if (!response.ok) {
        this.autoResumeTriggered = false
      }
    } catch {
      this.autoResumeTriggered = false
    }
  }

  private statusColor(status: string): string {
    switch (status) {
      case "completed": return "success"
      case "failed": return "danger"
      case "processing": return "primary"
      case "skipped": return "light border text-muted"
      default: return "secondary"
    }
  }

  private capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1)
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
